import React, { useState, useCallback, useContext } from "react";
import { Modal, ModalBody } from "reactstrap";
import Cropper from "react-easy-crop";
import getCroppedImg from "../../hooks/useCropImage";
import { Language } from "../../App";
import { RiCropLine, RiZoomInLine, RiZoomOutLine } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import "../feeds/postTabs/formTheme.css";

const ImageCropModal = ({ isOpen, toggle, imageSrc, onCropComplete, aspect }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const { lang } = useContext(Language);

  const onCropChange = (crop) => setCrop(crop);
  const onZoomChange = (zoom) => setZoom(zoom);

  const onCropCompleteHandler = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      const blobUrl = croppedImage;

      fetch(blobUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64Data = reader.result;
            onCropComplete(base64Data);
          };
          reader.readAsDataURL(blob);
        })
        .catch((error) => console.error(error));
      toggle();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      zIndex={1060}
      contentClassName="border-0 shadow-lg rounded-4 overflow-hidden"
      style={{ maxWidth: "560px" }}
    >
      {/* Modern Header */}
      <div 
        className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom"
        style={{ borderColor: "#EEF2F6", background: "#FFFFFF" }}
      >
        <div className="d-flex align-items-center gap-2.5">
          <div 
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: "34px", height: "34px", background: "#E0F7FC", color: "#00BADA" }}
          >
            <RiCropLine size={19} />
          </div>
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: "#0B132B", fontSize: "16px" }}>
              {lang.image_cropper || "Adjust & Crop Image"}
            </h5>
            <span className="text-muted" style={{ fontSize: "11.5px" }}>
              Drag to reposition and adjust zoom to fit
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={toggle}
          className="btn btn-sm btn-light rounded-circle p-1 d-flex align-items-center justify-content-center"
          style={{ width: "28px", height: "28px", color: "#64748B" }}
          aria-label="Close"
        >
          <IoClose size={18} />
        </button>
      </div>

      {/* Cropper Viewport & Controls */}
      <ModalBody className="p-3">
        <div 
          style={{ 
            position: "relative", 
            width: "100%", 
            height: "360px",
            background: "#0B132B",
            borderRadius: "12px",
            overflow: "hidden"
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteHandler}
          />
        </div>

        {/* Zoom Slider Control */}
        <div 
          className="d-flex align-items-center gap-3 px-3 py-2 mt-2 rounded-3"
          style={{ background: "#F8FAFC", border: "1px solid #EEF2F6" }}
        >
          <RiZoomOutLine size={17} color="#64748B" />
          <input
            type="range"
            value={zoom}
            min={1}
            max={3}
            step={0.05}
            aria-labelledby="Zoom"
            onChange={(e) => setZoom(Number(e.target.value))}
            className="form-range flex-grow-1"
            style={{ accentColor: "#00BADA", cursor: "pointer" }}
          />
          <RiZoomInLine size={17} color="#64748B" />
          <span className="fw-bold" style={{ fontSize: "11.5px", color: "#0096B3", minWidth: "36px" }}>
            {Math.round(zoom * 100)}%
          </span>
        </div>
      </ModalBody>

      {/* Modern Footer Actions */}
      <div 
        className="d-flex align-items-center justify-content-between px-4 py-3 border-top"
        style={{ borderColor: "#EEF2F6", background: "#FFFFFF" }}
      >
        <span className="text-muted d-none d-sm-inline" style={{ fontSize: "12px" }}>
          Aspect ratio locked for optimal display
        </span>
        <div className="d-flex align-items-center gap-2 ms-auto">
          <button
            type="button"
            className="pf-btn-cancel"
            onClick={toggle}
          >
            {lang.cancel || "Cancel"}
          </button>
          <button
            type="button"
            className="pf-btn-submit"
            onClick={handleCrop}
          >
            <span>{lang.save || "Apply & Save"}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ImageCropModal;
